import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { Box, Button, Checkbox, LinearProgress, TextField } from '@mui/material'
import { ui, tone } from '../../../utils/uiTokens'
import { capitalize } from '../../../utils/functions'
import { dateRange, ddmm, ddmmyyyy, todayIso } from '../../../utils/periodDates'
import { usePersistedState } from '../../../hooks/usePersistedState'
import { MONTHS } from '../../../components/monthWeekFilter/MonthWeekFilter'
import { curatorStudentsApiSlice } from '../../../features/curator/curatorStudents'
import { directorApi, DirSubjectPeriod, DirWeek } from '../../../features/director/directorApi'
import { Pill, btn, muted, sub } from '../ui'

const WEEKS = [1, 2, 3, 4]
type Manual = Record<string, { a?: string; b?: string }>

const DAY = 86400000
const toMs = (iso: string) => Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10))
const toIso = (ms: number) => new Date(ms).toISOString().slice(0, 10)
const addDays = (iso: string, n: number) => toIso(toMs(iso) + n * DAY)
const isWork = (iso: string) => {
    const d = new Date(toMs(iso)).getUTCDay()
    return d !== 0 && d !== 6
}

// 9 айдың апталары: қолмен енгізілмеген апта алдыңғысынан кейін 7 күн
export const buildCalendar = (start: string, manual: Manual) => {
    const weeks: Record<string, [string, string]> = {}
    let prevEnd = ''
    MONTHS.forEach((m) => {
        WEEKS.forEach((w) => {
            const o = manual[`${m}-${w}`] ?? {}
            const a = o.a || (prevEnd ? addDays(prevEnd, 1) : start)
            let b = o.b || addDays(a, 6)
            if (b < a) b = a
            weeks[`${m}-${w}`] = [a, b]
            prevEnd = b
        })
    })
    return weeks
}

// жалақы беру: 4-апта біткен соң келесі жұмыс күнінен бастап 5 жұмыс күні (сенбі/жексенбі есептелмейді)
export const payWindow = (monthEnd: string): [string, string] => {
    let d = addDays(monthEnd, 1)
    while (!isWork(d)) d = addDays(d, 1)
    const from = d
    let left = 4
    while (left > 0) {
        d = addDays(d, 1)
        if (isWork(d)) left--
    }
    return [from, d]
}

const manualOf = (weeks: DirWeek[] = []): Manual =>
    weeks
        .filter((w) => w.manual)
        .reduce((acc, w) => ({ ...acc, [`${w.monthNumber}-${w.weekNumber}`]: { a: w.startDate ?? undefined, b: w.endDate ?? undefined } }), {} as Manual)

const currentOf = (weeks: DirWeek[] = [], today: string) =>
    weeks.find((w) => w.startDate && w.endDate && w.startDate <= today && today <= w.endDate)

const PeriodsTab = () => {
    const { formatMessage, locale } = useIntl()
    const today = todayIso()
    const { data: courseResponse } = curatorStudentsApiSlice.useFetchCuratorCoursesQuery()
    const courses = courseResponse?.data ?? []
    const [courseId, setCourseId] = usePersistedState<number | null>('director.calCourse', null)
    const lek = courseId ?? courses[0]?.id ?? null
    const { data, isFetching } = directorApi.useGetPeriodsQuery({ courseId: lek ?? 0 }, { skip: !lek })
    const [approve, { isLoading: approving }] = directorApi.useApprovePeriodsMutation()
    const [unlock] = directorApi.useUnlockPeriodsMutation()

    const subjects = data?.subjects ?? []
    const free = subjects.filter((s) => !s.locked)
    const [selected, setSelected] = useState<number[]>([])
    const [start, setStart] = useState('')
    const [manual, setManual] = useState<Manual>({})
    const [editMonth, setEditMonth] = useState<number | null>(null)

    // черновик лека: дата старта курса и ручные недели первого утверждённого пәна
    useEffect(() => {
        const base = subjects.find((s) => s.locked)
        setStart(base?.courseStart || data?.courseStart || '')
        setManual(manualOf(base?.weeks))
        setSelected([])
        setEditMonth(null)
    }, [data])

    const weeks = useMemo(() => (start ? buildCalendar(start, manual) : null), [start, manual])
    const title = (s: { [k: string]: any }, base: string) => s[`${base}${capitalize(locale)}`] || s[`${base}Ru`]
    const curNow = weeks
        ? Object.entries(weeks).find(([, [a, b]]) => a <= today && today <= b)
        : undefined

    const setWeek = (m: number, w: number, edge: 'a' | 'b', v: string) =>
        setManual((prev) => ({ ...prev, [`${m}-${w}`]: { ...prev[`${m}-${w}`], [edge]: v || undefined } }))

    const onApprove = async () => {
        if (!lek || !start || !selected.length) return
        const list: DirWeek[] = Object.entries(manual)
            .filter(([, v]) => v.a || v.b)
            .map(([k]) => {
                const [m, w] = k.split('-').map(Number)
                const [a, b] = weeks![k]
                return { monthNumber: m, weekNumber: w, startDate: a, endDate: b, manual: true }
            })
        try {
            await approve({ courseId: lek, courseSubjectIds: selected, courseStart: start, weeks: list }).unwrap()
            toast.success(formatMessage({ id: 'dirPeriodsApproved' }))
        } catch { /* общий обработчик */ }
    }

    const onUnlock = async (s: DirSubjectPeriod) => {
        try {
            await unlock({ courseId: lek!, courseSubjectIds: [s.courseSubjectId] }).unwrap()
        } catch { /* общий обработчик */ }
    }

    return (
        <>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', marginBottom: '14px' }}>
                {courses.map((c) => (
                    <LekTab key={c.id} courseId={c.id} title={title(c, 'title')} active={c.id === lek} onClick={() => setCourseId(c.id)} />
                ))}
                <Box sx={{ ...sub, marginLeft: 'auto' }}>
                    {formatMessage({ id: 'dirTodayIs' })}: <b>{ddmmyyyy(today)}</b>
                </Box>
            </Box>

            {isFetching && <LinearProgress />}

            <Box sx={{ border: `1px solid ${ui.border}`, borderRadius: '14px', padding: { xs: '12px', md: '16px 18px' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', marginBottom: '12px' }}>
                    <Box>
                        <Box sx={{ fontWeight: 800, fontSize: '16px' }}>
                            {title(courses.find((c) => c.id === lek) ?? {}, 'title')} · {formatMessage({ id: 'dirPeriodsBlock' })}
                        </Box>
                        <Box sx={muted}>{formatMessage({ id: 'dirPeriodsBlockSub' })}</Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button
                            size="small"
                            variant="outlined"
                            sx={btn}
                            disabled={!free.length}
                            onClick={() => setSelected(selected.length === free.length ? [] : free.map((s) => s.courseSubjectId))}
                        >
                            {formatMessage({ id: selected.length === free.length && free.length ? 'dirUnselectAll' : 'dirSelectAll' })}
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            sx={btn}
                            onClick={() => {
                                setManual({})
                                setStart(data?.courseStart || '')
                            }}
                        >
                            {formatMessage({ id: 'dirResetDates' })}
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            sx={{ ...btn, background: ui.pink, '&:hover': { background: ui.pinkDark } }}
                            disabled={!selected.length || !start || approving}
                            onClick={onApprove}
                        >
                            {formatMessage({ id: 'dirApproveSelected' }, { n: selected.length })}
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginBottom: '12px' }}>
                    {subjects.map((s) => {
                        if (s.locked) {
                            const cur = currentOf(s.weeks, today)
                            return (
                                <Box key={s.courseSubjectId} sx={{ ...chip, background: tone.ok.bg, borderColor: tone.ok.border, color: tone.ok.text }}>
                                    ✓ <b>{title(s, 'subjectTitle')}</b>
                                    {cur && <span>· {formatMessage({ id: 'dirMonthWeek' }, { month: cur.monthNumber, week: cur.weekNumber })}</span>}
                                    <Box component="span" sx={{ textDecoration: 'underline', cursor: 'pointer', marginLeft: '4px' }} onClick={() => onUnlock(s)}>
                                        {formatMessage({ id: 'dirChange' }).toLowerCase()}
                                    </Box>
                                </Box>
                            )
                        }
                        const on = selected.includes(s.courseSubjectId)
                        return (
                            <Box
                                key={s.courseSubjectId}
                                component="label"
                                sx={{ ...chip, cursor: 'pointer', background: on ? ui.pinkSoft : ui.card, borderColor: on ? ui.pink : ui.border, color: on ? ui.pink : ui.ink }}
                            >
                                <Checkbox
                                    size="small"
                                    checked={on}
                                    sx={{ padding: 0, marginRight: '4px' }}
                                    onChange={(e) =>
                                        setSelected((prev) => (e.target.checked ? [...prev, s.courseSubjectId] : prev.filter((x) => x !== s.courseSubjectId)))
                                    }
                                />
                                <b>{title(s, 'subjectTitle')}</b>
                            </Box>
                        )
                    })}
                </Box>

                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <b>{formatMessage({ id: 'dirCourseStart' })}:</b>
                    <TextField size="small" type="date" value={start} onChange={(e) => setStart(e.target.value)} inputProps={{ style: { padding: '6px 8px' } }} />
                    <Box sx={{ ...muted, flex: 1, minWidth: 240 }}>
                        {curNow &&
                            `${formatMessage({ id: 'dirTodayIs' })}: ${formatMessage(
                                { id: 'dirMonthWeek' },
                                { month: curNow[0].split('-')[0], week: curNow[0].split('-')[1] },
                            )} (${dateRange(curNow[1][0], curNow[1][1])}). `}
                        {formatMessage({ id: 'dirCalendarHint' })}
                    </Box>
                </Box>

                {!weeks ? (
                    <Box sx={{ ...sub, padding: '12px 0' }}>{formatMessage({ id: 'dirNoCourseStart' })}</Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {MONTHS.map((m) => {
                            const a = weeks[`${m}-1`][0]
                            const e = weeks[`${m}-4`][1]
                            const [pa, pd] = payWindow(e)
                            const past = e < today
                            const cur = a <= today && today <= e
                            const editing = editMonth === m
                            return (
                                <React.Fragment key={m}>
                                    <Box sx={{ ...monthRow, opacity: past ? 0.55 : 1, borderColor: cur ? ui.pink : ui.border }}>
                                        <Box sx={{ minWidth: 110 }}>
                                            <b>{m} {formatMessage({ id: 'month' }).toLowerCase()}</b>
                                            <Box sx={muted}>{ddmm(a)} – {ddmm(e)}</Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1 }}>
                                            {WEEKS.map((w) => {
                                                const [x, y] = weeks[`${m}-${w}`]
                                                const now = x <= today && today <= y
                                                const man = !!(manual[`${m}-${w}`]?.a || manual[`${m}-${w}`]?.b)
                                                return (
                                                    <Box
                                                        key={w}
                                                        title={`${w} ${formatMessage({ id: 'week' }).toLowerCase()}${man ? ` · ${formatMessage({ id: 'dirManualWeek' })}` : ''}`}
                                                        sx={{
                                                            ...weekChip,
                                                            background: now ? ui.pink : ui.card,
                                                            color: now ? '#fff' : ui.ink,
                                                            borderColor: man ? ui.pink : ui.border,
                                                            borderWidth: man ? 2 : 1,
                                                        }}
                                                    >
                                                        <i style={{ fontStyle: 'normal', fontWeight: 800, marginRight: 4 }}>{w}</i>
                                                        {dateRange(x, y)}
                                                    </Box>
                                                )
                                            })}
                                        </Box>
                                        <Box sx={{ minWidth: 120 }}>
                                            <Box sx={muted}>{formatMessage({ id: 'headSalary' })}</Box>
                                            <b style={{ fontSize: 13 }}>{dateRange(pa, pd)}</b>
                                        </Box>
                                        <Box sx={{ minWidth: 64 }}>
                                            <Pill tone={past ? 'mute' : cur ? 'info' : 'good'}>
                                                {formatMessage({ id: past ? 'dirPast' : cur ? 'dirNow' : 'dirAhead' })}
                                            </Pill>
                                        </Box>
                                        <Button size="small" variant={editing ? 'contained' : 'outlined'} sx={btn} onClick={() => setEditMonth(editing ? null : m)}>
                                            {formatMessage({ id: editing ? 'dirDone' : 'dirChange' })}
                                        </Button>
                                    </Box>
                                    {editing && (
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 1, padding: '8px 12px 12px', background: ui.surface, borderRadius: '10px' }}>
                                            {WEEKS.map((w) => {
                                                const [x, y] = weeks[`${m}-${w}`]
                                                return (
                                                    <Box key={w} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                        <b style={{ minWidth: 56, fontSize: 13 }}>{w} {formatMessage({ id: 'week' }).toLowerCase()}</b>
                                                        <TextField size="small" type="date" value={x} onChange={(ev) => setWeek(m, w, 'a', ev.target.value)} inputProps={{ style: { padding: '5px 6px', fontSize: 12.5 } }} />
                                                        <span>—</span>
                                                        <TextField size="small" type="date" value={y} onChange={(ev) => setWeek(m, w, 'b', ev.target.value)} inputProps={{ style: { padding: '5px 6px', fontSize: 12.5 } }} />
                                                    </Box>
                                                )
                                            })}
                                        </Box>
                                    )}
                                </React.Fragment>
                            )
                        })}
                    </Box>
                )}
            </Box>
        </>
    )
}

// таб лека с ✓, если все пәны лека утверждены
const LekTab: React.FC<{ courseId: number; title: string; active: boolean; onClick: () => void }> = ({ courseId, title, active, onClick }) => {
    const { data } = directorApi.useGetPeriodsQuery({ courseId })
    const done = !!data?.subjects?.length && data.subjects.every((s) => s.locked)
    return (
        <Box
            onClick={onClick}
            sx={{
                padding: '8px 14px',
                borderRadius: ui.radius,
                cursor: 'pointer',
                fontSize: '13.5px',
                fontWeight: active ? 700 : 500,
                color: active ? ui.pink : ui.sub,
                background: active ? ui.pinkSoft : 'transparent',
                border: `1px solid ${active ? 'transparent' : ui.border}`,
            }}
        >
            {title}
            {done && ' ✓'}
        </Box>
    )
}

const chip = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 10px',
    border: '1px solid',
    borderRadius: '10px',
    fontSize: '13px',
}

const monthRow = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    flexWrap: 'wrap',
    padding: '10px 12px',
    border: '1px solid',
    borderRadius: '10px',
}

const weekChip = {
    padding: '4px 8px',
    borderRadius: '8px',
    border: '1px solid',
    fontSize: '12.5px',
    whiteSpace: 'nowrap',
}

export default PeriodsTab
